/**
 * Poseidon dashboard — KPI card grid (§3.2 A).
 *
 * The grid renders the cards the daemon sends, ordered by the spec's list. A
 * metric the daemon cannot currently compute is rendered as an explicit
 * unavailable card with the reason, never as a zero and never omitted.
 */
import type { ReactNode } from 'react';
import type { KpiCard, KpiSnapshot } from '../api/types';
import { KPI_ORDER } from '../lib/labels';
import { cx } from '../lib/cx';
import type { Tone } from '../lib/staleness';
import { Badge } from './primitives';

const TONE_CLASS: Record<KpiCard['tone'], string> = {
  neutral: '',
  ok: 'text-ok',
  positive: 'text-ok',
  negative: 'text-danger',
  warning: 'text-warn',
  critical: 'text-danger',
  muted: 'text-faint',
};

function KpiTile({ card }: { card: KpiCard }): JSX.Element {
  const unavailable = card.availability !== 'ok';
  return (
    <article className={cx('kpi', unavailable && 'kpi--unavailable')} title={card.help ?? undefined}>
      <span className="kpi__label">
        {card.label}
        {card.lower_is_better && <span title="Lower is better for this metric" aria-label="lower is better">↓</span>}
        {card.availability === 'stale' && <Badge tone="warn">stale</Badge>}
      </span>
      <span className={cx('kpi__value', TONE_CLASS[card.tone])}>{card.display}</span>
      {card.sublabel !== null && <span className="kpi__sub">{card.sublabel}</span>}
      {unavailable && (
        <span className="kpi__unavailable">
          {card.availability === 'unavailable' ? 'unavailable' : 'stale'}
          {card.unavailable_reason !== null && <>: {card.unavailable_reason}</>}
        </span>
      )}
    </article>
  );
}

export function KpiGrid({ snapshot, mock = false }: { snapshot: KpiSnapshot; mock?: boolean }): JSX.Element {
  const byId = new Map(snapshot.cards.map((card) => [card.id, card]));
  const ordered = KPI_ORDER.map((id) => byId.get(id)).filter((card): card is KpiCard => card !== undefined);

  // Any card the daemon sends that this build does not know about is surfaced
  // rather than dropped, so a protocol addition is visible instead of invisible.
  const unknownCards = snapshot.cards.filter((card) => !KPI_ORDER.includes(card.id));

  return (
    <div className="grid" style={{ gap: 10 }}>
      <div className="data-clock">
        <span className="text-faint">Session</span>
        <span className="text-mono">{snapshot.session_date}</span>
        <span className="text-faint">· breakeven threshold</span>
        <span className="text-mono">
          {snapshot.breakeven_threshold} {snapshot.account_currency}
        </span>
        {mock && <span className="mock-note">mock values</span>}
      </div>
      <div className="grid grid--kpi">
        {ordered.map((card) => (
          <KpiTile key={card.id} card={card} />
        ))}
      </div>
      {unknownCards.length > 0 && (
        <div className="text-warn" style={{ fontSize: 12 }}>
          The daemon sent {unknownCards.length} KPI card(s) this build does not recognise ({unknownCards.map((card) => card.id).join(', ')}).
          They are shown below to avoid hiding data.
          <div className="grid grid--kpi" style={{ marginTop: 8 }}>
            {unknownCards.map((card) => (
              <KpiTile key={String(card.id)} card={card} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Compact indicator used in table cells and side panels. */
export function ToneText({ tone, children }: { tone: Tone; children: ReactNode }): JSX.Element {
  return <span className={cx(tone === 'ok' ? 'text-ok' : tone === 'warn' ? 'text-warn' : tone === 'danger' ? 'text-danger' : 'text-muted')}>{children}</span>;
}
